import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
import axios from '../../../utils/axios';
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import Popover from '../../../components/gps/Popover';
import { AvatarIniciais } from '../../../components/gps';
import { perfilLabel } from '../../../utils/perfil';

/* ============================================================================
   Menu da conta - visual novo.

   Substitui IconButtonAnimate + MenuPopover + MenuList/MenuItem do MUI. O logout
   e identico: `await logout()` e `router.replace(PATH_AUTH.login)`, com o guarda
   de componente montado antes de fechar - nada de autenticacao foi tocado.

   QUATRO COISAS QUE MUDAM:

   1. O GATILHO MOSTRA O NOME. Antes o nome ficava num `<span>` ao lado do botao:
      clicar nele nao abria nada, e essa e a parte que as pessoas clicam.

   2. `user?.displayName` -> `user?.nome`. O cabecalho do painel mostrava
      `displayName`, campo do template que a API nunca preenche; ficava em branco
      em cima do e-mail. `nome` e o que o backend devolve (e o que a faixa superior
      ja exibia ao lado).

   3. PERFIL VEM DE `perfilLabel`. O codigo antigo tinha tres `if` para 1, 2 e 0 -
      Master (3) caia fora e o painel simplesmente nao mostrava perfil nenhum para
      quem tem o perfil mais alto do sistema.

   4. "Sair" e um item de risco: fica separado por linha e em vermelho, como toda
      acao destrutiva do visual novo.
   ============================================================================ */

export default function AccountPopover({ nome }) {
  const router = useRouter();
  const { user, logout } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar } = useSnackbar();

  const nomeExibido = nome || user?.nome || user?.displayName || '—';
  const codigo = Number(user?.codigo || user?.id || 0);
  const [foto, setFoto] = useState(null);

  /* A foto do perfil, quando existe, substitui as iniciais - aqui e na tela de
     Perfil, que e onde ela e enviada.

     Vem em base64 num JSON (`GET /users/foto/:codigo`) e nao como URL de imagem:
     toda rota da API exige `Authorization`, e <img src> do navegador nao manda
     cabecalho - servir os bytes exigiria aceitar o token na query string, o que
     poe credencial em log de servidor. Ver users.controller.ts.

     Sem foto o endpoint responde `{ foto: null }`, que nao e erro: cai nas
     iniciais em silencio. */
  useEffect(() => {
    if (!codigo) return;
    let cancelado = false;
    axios
      .get(`/users/foto/${codigo}`)
      .then((r) => {
        if (!cancelado) setFoto(r?.data?.foto || null);
      })
      .catch(() => {
        if (!cancelado) setFoto(null);
      });
    return () => {
      cancelado = true;
    };
  }, [codigo]);

  const avatar = (classe) =>
    foto ? (
      <img src={foto} alt="" className={`${classe} rounded-md border border-line object-cover`} />
    ) : (
      <AvatarIniciais nome={nomeExibido} className={classe} />
    );

  const sair = async (fechar) => {
    try {
      await logout();
      router.replace(PATH_AUTH.login);
      if (isMountedRef.current) fechar();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Não foi possível sair.', { variant: 'error' });
    }
  };

  return (
    <Popover
      rotuloGatilho="Minha conta"
      largura="w-[260px]"
      gatilho={
        <>
          {/* `border-primary-foreground/40 bg-primary-foreground/10` no gatilho: o avatar de iniciais tem
              fundo `bg-soft` e texto `text-muted-foreground` por padrao, que sobre a
              faixa colorida vira um quadradinho cinza com texto ilegivel. */}
          {avatar('h-8 w-8 text-[12px] border-primary-foreground/40 bg-primary-foreground/10 text-primary-foreground')}
          <span className="hidden max-w-[160px] truncate text-[12px] font-semibold sm:block">
            {nomeExibido}
          </span>
          <span className="text-[10px] text-faint" aria-hidden>
            ▾
          </span>
        </>
      }
    >
      {(fechar) => (
        <>
          <div className="px-3 py-2.5">
            <div className="truncate text-[12.5px] font-bold">{nomeExibido}</div>
            {user?.email && <div className="truncate text-[12px] text-muted-foreground">{user.email}</div>}
            <div className="mt-1 inline-flex h-[19px] items-center rounded bg-soft px-1.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
              {perfilLabel(user?.perfil)}
            </div>
          </div>

          <div className="border-t border-line">
            {/* Caminho para o perfil. Antes deste item a tela de Perfil nao tinha
                entrada em lugar nenhum - e o menu da conta e onde se procura por
                "meus dados", nao no menu lateral de cadastros. */}
            <button
              type="button"
              onClick={() => {
                fechar();
                router.push(PATH_DASHBOARD.general.perfil);
              }}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12.5px] font-semibold text-ink hover:bg-soft"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden className="text-muted-foreground">
                <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
                <path
                  d="M4.5 20c0-3.6 3.4-5.5 7.5-5.5s7.5 1.9 7.5 5.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              Meu perfil
            </button>
          </div>

          <div className="border-t border-line">
            <button
              type="button"
              onClick={() => sair(fechar)}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-[12.5px] font-semibold text-danger hover:bg-danger-soft"
            >
              <span aria-hidden>⤴</span> Sair
            </button>
          </div>
        </>
      )}
    </Popover>
  );
}
